import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import ReportTemplate from '../../components/reports/ReportTemplate';
import DateRangePicker from '../../components/reports/DateRangePicker';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ROUTES } from '../../consts/general';
import { AMAZON_AD_SPEND_DETAIL } from '../../consts/reports';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import useDateRangeParams from '../../hooks/useDateRangeParams';

const PAGE_SIZE = 50;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const formatValue = (value, key) => {
  if (value === null || value === undefined || value === '') return '—';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  return ['total_cost', 'sales', 'unallocated_cost'].includes(key)
    ? numeric.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
    : numeric.toLocaleString('en-US');
};

const AmazonAdSpendDetailReport = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);
  const today = new Date();
  const defaultStartDate = format(
    new Date(today.getFullYear(), today.getMonth(), 1),
    'yyyy-MM-dd',
  );
  const defaultEndDate = format(
    new Date(today.getFullYear(), today.getMonth() + 1, 0),
    'yyyy-MM-dd',
  );
  const { startDate, endDate, setDateRange } = useDateRangeParams(defaultStartDate, defaultEndDate);
  const [sku, setSku] = useState('');
  const [campaign, setCampaign] = useState('');
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [totals, setTotals] = useState({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const params = useCallback((includePage = true) => {
    const query = new URLSearchParams({ startDate, endDate });
    if (includePage) {
      query.set('page', page);
      query.set('pageSize', PAGE_SIZE);
    }
    if (sku) query.set('sku', sku);
    if (campaign) query.set('campaign', campaign);
    return query;
  }, [startDate, endDate, page, sku, campaign]);

  const load = useCallback(async () => {
    const { signal, requestId } = beginRequest();
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `/api/v1/reports/${AMAZON_AD_SPEND_DETAIL}?${params()}`,
        { headers: authHeaders(), signal },
      );
      const json = await response.json();
      if (!response.ok) throw new Error(json.error || 'Failed to load report');
      if (!isLatest(requestId)) return;
      setData(json.data || []);
      setColumns(json.columns || []);
      setTotals(json.totals || {});
      setTotalRecords(json.totalRecords || 0);
      setTotalPages(json.totalPages || 1);
    } catch (requestError) {
      if (requestError?.name === 'AbortError' || !isLatest(requestId)) return;
      setError(requestError.message);
      setData([]);
    } finally {
      if (isLatest(requestId)) setLoading(false);
    }
  }, [beginRequest, isLatest, params]);

  useEffect(() => {
    if (!initialReady) return;
    load();
  }, [initialReady, load]);

  useEffect(() => {
    setPage(1);
  }, [startDate, endDate, sku, campaign]);

  const exportData = async () => {
    const response = await fetch(
      `/api/v1/reports/${AMAZON_AD_SPEND_DETAIL}/export?${params(false)}`,
      { headers: authHeaders() },
    );
    if (!response.ok) throw new Error('Failed to export report');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'amazon-ad-spend-detail.csv';
    link.click();
    URL.revokeObjectURL(url);
    return { handled: true };
  };

  const filters = (
    <div className="contents">
      <input
        className="rounded border px-3 py-2 text-sm"
        placeholder="SKU"
        value={sku}
        onChange={(event) => {
          setSku(event.target.value);
          setPage(1);
        }}
      />
      <input
        className="rounded border px-3 py-2 text-sm"
        placeholder="Campaign name"
        value={campaign}
        onChange={(event) => {
          setCampaign(event.target.value);
          setPage(1);
        }}
      />
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        onDateChange={({ startDate: start, endDate: end }) => {
          setDateRange({ startDate: start, endDate: end });
          setPage(1);
        }}
        label="Period overlap date range"
      />
    </div>
  );

  const pagination = (
    <div className="flex items-center justify-between text-sm">
      <span>{totalRecords.toLocaleString()} rows</span>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => setPage((value) => value - 1)}
          className="rounded border px-3 py-1 disabled:opacity-50"
        >
          Previous
        </button>
        <span className="py-1">Page {page} of {totalPages}</span>
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => setPage((value) => value + 1)}
          className="rounded border px-3 py-1 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );

  return (
    <ReportTemplate
      reportId={AMAZON_AD_SPEND_DETAIL}
      title="Amazon Ad Spend Detail"
      description="Amazon advertising rows. Period filtering includes any row whose reporting period overlaps the selected range; NULL advertised SKUs remain visible as (unallocated)."
      breadcrumbs={[{ label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> }, { label: '' }]}
      onBack={() => navigate(ROUTES.REPORTS)}
      marginFromSidebar={marginFromSidebar}
      isLoading={loading}
      error={error}
      filters={filters}
      exportButton={<CSVExportButton data={data} columns={columns} filename="amazon-ad-spend-detail" onExport={exportData} />}
      pagination={pagination}
      renderChildrenOnly
    >
      <div className="space-y-3">
        <div className="grid gap-3 rounded border bg-white p-4 md:grid-cols-4">
          {[
            ['total_cost', 'Total cost'],
            ['sales', 'Sales'],
            ['units_sold', 'Units sold'],
            ['purchases', 'Purchases'],
            ['impressions', 'Impressions'],
            ['clicks', 'Clicks'],
            ['detail_page_views', 'Detail page views'],
            ['unallocated_cost', 'Unallocated cost'],
            ['unallocated_rows', 'Unallocated rows'],
          ].map(([key, label]) => (
            <div key={key} className="rounded bg-gray-50 p-3">
              <div className="text-xs text-gray-500">{label}</div>
              <div className="font-semibold">{formatValue(totals[key], key)}</div>
            </div>
          ))}
        </div>
        <div className="overflow-auto rounded border bg-white">
          {data.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0h2a2 2 0 002-2m0 0V5a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No Data</h3>
              <p className="mt-2 text-sm text-gray-500">There are no Amazon ad-spend rows in the selected range.</p>
            </div>
          ) : (
            <table className="min-w-max text-left text-xs">
              <thead className="sticky top-0 bg-gray-100">
                <tr>
                  {columns.map((column) => (
                    <th key={column.key} className="whitespace-nowrap px-3 py-2">{column.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, index) => (
                  <tr key={`${row.period_start}-${index}`} className={index % 2 ? 'bg-gray-50' : ''}>
                    {columns.map((column) => (
                      <td key={column.key} className="whitespace-nowrap px-3 py-2">
                        {formatValue(row[column.key], column.key)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </ReportTemplate>
  );
};

export default AmazonAdSpendDetailReport;
